import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  KeyRound,
  Pencil,
  Plus,
  Shield,
  ShieldCheck,
  ShieldOff,
  X,
} from "lucide-react";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { initials } from "@/lib/utils";
import {
  useAssignRoles,
  useRemoveRole,
  useUpdateUser,
  useUser,
} from "../hooks/useUsers";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { StatusBadge } from "../components/StatusBadge";
import { UserForm } from "../components/UserForm";
import { RoleAssignmentModal } from "../components/RoleAssignmentModal";
import { AssignmentsTab } from "../components/AssignmentsTab";
import { ProductAccessTab } from "../components/ProductAccessTab";
import { ResetPasswordDialog } from "../components/ResetPasswordDialog";
import { ResetMfaDialog } from "../components/ResetMfaDialog";

type Tab = "details" | "roles" | "assignments" | "product_access";

export default function UserDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: user, isLoading, isError } = useUser(id);

  const [tab, setTab] = useState<Tab>("details");
  const [modalOpen, setModalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetMfaOpen, setResetMfaOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(params.get("edit") === "true");
  const { user: authUser } = useAuth();
  const canAdminUsers =
    !!authUser &&
    (authUser.roles.some((r) => r.name === "Super Admin") ||
      authUser.permissions["user_management"]?.canAdmin === true ||
      authUser.permissions["user_management"]?.canEdit === true);

  const updateUser = useUpdateUser(id);
  const assignRoles = useAssignRoles(id);
  const removeRole = useRemoveRole(id);

  function exitEditMode() {
    setEditMode(false);
    if (params.has("edit")) {
      params.delete("edit");
      setParams(params, { replace: true });
    }
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <p className="text-sm text-muted-foreground">Loading user…</p>
        </div>
      </AppLayout>
    );
  }

  if (isError || !user) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <p className="text-sm text-destructive">Failed to load user.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "User Management", href: "/admin/users" },
        { label: user.name },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/users")}>
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Shield className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">
              {editMode ? "Edit User" : "View User"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {canAdminUsers && authUser?.id !== user.id && (
              <Button
                variant="outline"
                onClick={() => setResetOpen(true)}
              >
                <KeyRound className="mr-1 h-4 w-4" strokeWidth={1.5} />
                Reset Password
              </Button>
            )}
            {canAdminUsers && authUser?.id !== user.id && user.mfaEnabled && (
              <Button
                variant="outline"
                onClick={() => setResetMfaOpen(true)}
              >
                <ShieldOff className="mr-1 h-4 w-4" strokeWidth={1.5} />
                Reset 2FA
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              aria-label={editMode ? "Stop editing" : "Edit"}
              onClick={() => {
                if (editMode) {
                  exitEditMode();
                } else {
                  setEditMode(true);
                  setTab("details");
                }
              }}
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="History"
              onClick={() => setHistoryOpen(true)}
            >
              <Clock className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
        {toast && (
          <div
            className="rounded-md border px-4 py-2 text-sm font-medium"
            style={{
              borderColor: "hsl(142,55%,80%)",
              background: "hsl(142,55%,96%)",
              color: "hsl(142,72%,28%)",
            }}
          >
            {toast}
          </div>
        )}

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-5">
              <Avatar src={user.avatarUrl} fallback={initials(user.name)} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold tracking-tight">{user.name}</h2>
                  <StatusBadge status={user.status} />
                  {user.mfaEnabled && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                      style={{ background: "hsl(142,55%,94%)", color: "hsl(142,72%,28%)" }}
                      title="Two-factor authentication enabled"
                    >
                      <ShieldCheck className="h-3 w-3" strokeWidth={1.8} />
                      2FA
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  {user.code && <ReferenceIdBadge id={user.code} />}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {user.roles.map((role) => (
                    <Badge key={role.id}>{role.name}</Badge>
                  ))}
                </div>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                <div className="text-2xl font-semibold text-foreground">
                  {user.roles.length}
                </div>
                <div>roles assigned</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="border-b border-border">
          <nav className="-mb-px flex gap-6">
            <button
              type="button"
              onClick={() => setTab("details")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                tab === "details"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Details
            </button>
            <button
              type="button"
              onClick={() => setTab("roles")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                tab === "roles"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Role Assignment
            </button>
            <button
              type="button"
              onClick={() => setTab("assignments")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                tab === "assignments"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Assignments
            </button>
            <button
              type="button"
              onClick={() => setTab("product_access")}
              className={`border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                tab === "product_access"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              Product Access
            </button>
          </nav>
        </div>

        {tab === "details" ? (
          <Card>
            <CardContent className="pt-6">
              <UserForm
                user={user}
                isSubmitting={updateUser.isPending}
                readOnly={!editMode}
                onCancel={exitEditMode}
                onSubmit={(payload) =>
                  updateUser.mutate(payload, {
                    onSuccess: () => exitEditMode(),
                  })
                }
              />
            </CardContent>
          </Card>
        ) : tab === "assignments" ? (
          <Card>
            <CardContent className="pt-6">
              <AssignmentsTab user={user} />
            </CardContent>
          </Card>
        ) : tab === "product_access" ? (
          <Card>
            <CardContent className="pt-6">
              <ProductAccessTab userId={user.id} />
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="space-y-3 pt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">Assigned roles</h3>
                <Button onClick={() => setModalOpen(true)}>
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  Assign Role
                </Button>
              </div>
              <div className="space-y-2">
                {user.roles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No roles assigned.</p>
                ) : (
                  user.roles.map((role) => (
                    <div
                      key={role.id}
                      className="flex items-center justify-between rounded-md border border-border p-3"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{role.name}</span>
                          {role.isSystem && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                              System
                            </span>
                          )}
                        </div>
                        {role.description && (
                          <p className="text-xs text-muted-foreground">{role.description}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={user.roles.length <= 1 || removeRole.isPending}
                        onClick={() => removeRole.mutate(role.id)}
                        aria-label={`Remove role ${role.name}`}
                      >
                        <X className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <RoleAssignmentModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          currentRoleIds={user.roles.map((r) => r.id)}
          isSubmitting={assignRoles.isPending}
          onConfirm={(roleIds) => {
            assignRoles.mutate(roleIds, {
              onSuccess: () => setModalOpen(false),
            });
          }}
        />

        <ActivityHistoryDrawer
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="user"
          entityId={user.id}
          entityLabel={user.name}
        />

        <ResetPasswordDialog
          open={resetOpen}
          onClose={() => setResetOpen(false)}
          targetUserId={user.id}
          targetName={user.name}
          onSuccess={() => {
            setToast(`Password reset for ${user.name}. Share securely.`);
            setTimeout(() => setToast(null), 4000);
          }}
        />

        <ResetMfaDialog
          open={resetMfaOpen}
          onClose={() => setResetMfaOpen(false)}
          targetUserId={user.id}
          targetName={user.name}
          onSuccess={() => {
            setToast(`Two-factor authentication reset for ${user.name}.`);
            setTimeout(() => setToast(null), 4000);
          }}
        />
      </div>
    </AppLayout>
  );
}
